import { useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';

import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent
} from '@stripe/stripe-js';

import { EditButton } from './EditButton';
import { get, post, put } from '../../../../libs/Requests';
import { InViewSpinner } from '../../../../components/Spinners/InViewSpinner';
import { DataLoader, LoadingStatus } from '../../../../types/DataLoader';
import { CardCVC } from '../../../order/components/OrderForm/CardCVC';
import { CardExpiry } from '../../../order/components/OrderForm/CardExpiry';
import { CardNumber } from '../../../order/components/OrderForm/CardNumber';

import { ICardDTO } from '../../../../../../types/CardDTO';

export enum OrganizationAddressFormMode {
  EditModeEnabled = 'edit-mode-enabled',
  OrganizationAddressEdited = 'organization-address-edited',
  OrganizationAddressSaved = 'organization-address-saved'
}

export interface OrganizationAddressDataLoader extends DataLoader {
  data?: ICardDTO;
}

export function OrganizationAddressForm() {
  const [error, setError] = useState('');
  const [fetchData, setFetchData] = useState(true);
  const [, setMode] = useState(OrganizationAddressFormMode.OrganizationAddressSaved);
  const [editable, setEditable] = useState(false);
  const [dataLoader, setDataLoader] = useState<OrganizationAddressDataLoader>({
    loadingStatus: LoadingStatus.Loading,
    data: {}
  });

  const validity = {
    cardNumber: false,
    cvc: false,
    exp: false
  };

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    get('/company/cards').then(async (data) => {
      if (data.ok) {
        const card: ICardDTO = await data.json();

        setDataLoader({
          ...dataLoader,
          data: card,
          loadingStatus: LoadingStatus.Succeed
        });
      }
    });

    setFetchData(false);
  }, [fetchData]);

  function handleCardNumberChange(event: StripeCardNumberElementChangeEvent) {
    setError('');

    validity.cardNumber = event.complete && !event.error;
  }

  function handleCardCvcChange(event: StripeCardCvcElementChangeEvent) {
    setError('');

    validity.cvc = event.complete && !event.error;
  }

  function handleCardExpChange(event: StripeCardExpiryElementChangeEvent) {
    setError('');

    validity.exp = event.complete && !event.error;
  }

  async function submitForm() {
    if (!validity.cardNumber || !validity.cvc || !validity.exp) {
      return;
    }

    let serverResponse;
    try {
      serverResponse = await post('/company/cards', '');

      serverResponse = await serverResponse.json();
    } catch (postCardError) {
      console.error(postCardError);
    }

    if (serverResponse && serverResponse.clientSecret) {
      const cardNumberElement = elements!.getElement('cardNumber');

      const card: ICardDTO = dataLoader.data!;

      const resp = await stripe!.confirmCardSetup(serverResponse.clientSecret, {
        payment_method: {
          card: cardNumberElement!,
          metadata: {
            addingCard: 1
          },
          billing_details: {
            name: card.billingDetails?.name,
            email: card.billingDetails?.email,
            address: {
              city: card.billingDetails?.address?.city,
              country: card.billingDetails?.address?.country,
              line1: card.billingDetails?.address?.line1,
              line2: card.billingDetails?.address?.line2,
              postal_code: card.billingDetails?.address?.postalCode,
              state: card.billingDetails?.address?.state
            }
          }
        }
      });

      if (resp.setupIntent) {
        setMode(OrganizationAddressFormMode.OrganizationAddressSaved);
        setEditable(false);

        setDataLoader({
          ...dataLoader,
          loadingStatus: LoadingStatus.Loading
        });

        await put(
          '/company/cards',
          JSON.stringify({
            paymentMethod: resp.setupIntent.payment_method
          })
        );

        setFetchData(true);
      }
    }
  }

  if (dataLoader.loadingStatus === LoadingStatus.Loading || !dataLoader.data) {
    return <></>;
  }

  return (
    <div className="w-full">
      <div className="flex justify-between py-4">
        <span className="font-semibold">Payment Method</span>

        <EditButton
          disabled={dataLoader.loadingStatus !== LoadingStatus.Succeed}
          isInEditMode={editable}
          onCancel={() => {
            setMode(OrganizationAddressFormMode.OrganizationAddressSaved);
            setEditable(false);
          }}
          onSave={() => {
            submitForm();
          }}
          onEdit={() => {
            setMode(OrganizationAddressFormMode.EditModeEnabled);
            setEditable(true);
          }}></EditButton>
      </div>

      {dataLoader.loadingStatus != LoadingStatus.Succeed || error ? (
        <div>{dataLoader.error || error || <InViewSpinner></InViewSpinner>}</div>
      ) : null}

      {dataLoader.loadingStatus === LoadingStatus.Succeed && stripe && elements ? (
        <div className="flex flex-col space-x-4 md:flex-row">
          <div className="w-full pr-2 md:w-1/2">
            <div>
              <div className="w-full">
                <label
                  className="text-xs font-semibold text-[#787878]"
                  htmlFor="cardNum">
                  Card Information
                </label>

                <CardNumber
                  onInit={(cardNumber) => {
                    cardNumber.on('change', (event) => {
                      handleCardNumberChange(event);
                    });
                  }}
                  disabled={!editable}
                  elements={elements}
                  last4={dataLoader.data!.last4}></CardNumber>
              </div>
            </div>
            <div className="flex w-full flex-col md:flex-row md:space-x-4">
              <div className="w-full">
                <label
                  className="text-xs font-semibold text-[#787878]"
                  htmlFor="cardCvc">
                  CVC
                </label>

                <CardCVC
                  onInit={(cardCvc) => {
                    cardCvc.on('change', (event) => {
                      handleCardCvcChange(event);
                    });
                  }}
                  elements={elements}
                  disabled={!editable}></CardCVC>
              </div>

              <div className="w-full">
                <label
                  className="text-xs font-semibold text-[#787878]"
                  htmlFor="cardExpiration">
                  MM / YY
                </label>

                <CardExpiry
                  onInit={(cardExp) => {
                    cardExp.on('change', (event) => {
                      handleCardExpChange(event);
                    });
                  }}
                  elements={elements}
                  cardExpiry={`${dataLoader.data?.expMonth} / ${dataLoader.data?.expYear?.toString().slice(2)}`}
                  disabled={!editable}></CardExpiry>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
